import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription, filter, fromEvent, interval, merge, timer } from 'rxjs';

/** Chiave namespaced della guardia anti-loop del reload automatico. */
export const SW_UNRECOVERABLE_RELOAD_KEY = 'amsuite-sw-unrecoverable-reload-at';
export const SW_UPDATE_PROMPT_COOLDOWN_MS = 15 * 60_000;
export const SW_UPDATE_CHECK_INTERVAL_MS = 6 * 60 * 60_000;
const UNRECOVERABLE_RELOAD_DELAY_MS = 3000;
const UNRECOVERABLE_RELOAD_COOLDOWN_MS = 60_000;

/** Gestisce aggiornamenti e recovery del service worker della shell AmSuite. */
@Injectable({ providedIn: 'root' })
export class SwUpdateService {
  private readonly _swUpdate = inject(SwUpdate);
  private readonly _confirmationService = inject(ConfirmationService);
  private readonly _messageService = inject(MessageService);
  private readonly _translate = inject(TranslateService);
  private readonly _destroyRef = inject(DestroyRef);

  private _checkInFlight?: Promise<void>;
  private _latestVersionHash?: string;
  private _promptOpen = false;
  private _promptDismissedAt = 0;
  private _reloadScheduled = false;
  private _repromptSubscription?: Subscription;

  readonly updateAvailable = signal(false);

  constructor() {
    if (!this._swUpdate.isEnabled) return;

    this._swUpdate.versionUpdates.pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(event => {
      if (event.type === 'VERSION_INSTALLATION_FAILED') {
        this._handleInstallationFailed();
        return;
      }
      if (event.type === 'VERSION_READY') {
        this._handleVersionReady(event);
      }
    });

    this._swUpdate.unrecoverable.pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(() => this._handleUnrecoverable());

    merge(
      fromEvent(window, 'online'),
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'visible')
      ),
      interval(SW_UPDATE_CHECK_INTERVAL_MS).pipe(
        filter(() => document.visibilityState === 'visible')
      )
    ).pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(() => this._handleLifecycleTrigger());
  }

  /** Non propaga errori di rete e condivide un eventuale check gia' in corso. */
  async checkForUpdate(): Promise<void> {
    if (!this._swUpdate.isEnabled) return;
    if (this._checkInFlight) return this._checkInFlight;

    const pending = this._checkForUpdateSafely();
    this._checkInFlight = pending;
    try {
      await pending;
    } finally {
      if (this._checkInFlight === pending) this._checkInFlight = undefined;
    }
  }

  private async _checkForUpdateSafely(): Promise<void> {
    try {
      await this._swUpdate.checkForUpdate();
    } catch {
      // Il prossimo trigger online/visibility/intervallo ritentera' il check.
    }
  }

  private _handleVersionReady(event: VersionReadyEvent): void {
    if (this._latestVersionHash !== event.latestVersion.hash) {
      this._latestVersionHash = event.latestVersion.hash;
      this._promptDismissedAt = 0;
      this._cancelReprompt();
    }
    this.updateAvailable.set(true);
    if (this._isEmbeddedIframe()) {
      void this._activateAndReload();
      return;
    }
    this._maybePromptUpdate();
  }

  private _handleLifecycleTrigger(): void {
    this._maybePromptUpdate();
    void this.checkForUpdate();
  }

  private _handleUnrecoverable(): void {
    // Il primo toast resta visibile fino al reload gia' pianificato: evita duplicati
    // durante i tre secondi di attesa.
    if (this._reloadScheduled) return;

    if (!this._canAutoReload()) {
      this._messageService.add({
        key: 'pwa',
        severity: 'warn',
        summary: this._translate.instant('pwa.unrecoverable-title'),
        detail: this._translate.instant('pwa.unrecoverable-manual-message'),
        sticky: true
      });
      return;
    }

    this._messageService.add({
      key: 'pwa',
      severity: 'warn',
      summary: this._translate.instant('pwa.unrecoverable-title'),
      detail: this._translate.instant('pwa.unrecoverable-message'),
      life: UNRECOVERABLE_RELOAD_DELAY_MS
    });
    this._reloadScheduled = true;
    timer(UNRECOVERABLE_RELOAD_DELAY_MS).pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(() => this._reload());
  }

  private _canAutoReload(): boolean {
    try {
      const last = Number(sessionStorage.getItem(SW_UNRECOVERABLE_RELOAD_KEY) ?? '0');
      if (Date.now() - last < UNRECOVERABLE_RELOAD_COOLDOWN_MS) return false;
      sessionStorage.setItem(SW_UNRECOVERABLE_RELOAD_KEY, String(Date.now()));
      return true;
    } catch {
      // Senza storage non e' possibile garantire l'anti-loop: recovery solo manuale.
      return false;
    }
  }

  private _maybePromptUpdate(): void {
    if (
      !this.updateAvailable() ||
      this._promptOpen ||
      document.visibilityState !== 'visible' ||
      Date.now() - this._promptDismissedAt < SW_UPDATE_PROMPT_COOLDOWN_MS
    ) {
      return;
    }

    this._promptOpen = true;
    const promptedVersionHash = this._latestVersionHash;
    this._confirmationService.confirm({
      key: 'pwa-update',
      header: this._translate.instant('pwa.update-available'),
      message: this._translate.instant('pwa.update-message'),
      icon: 'pi pi-refresh',
      acceptLabel: this._translate.instant('pwa.update-action'),
      rejectLabel: this._translate.instant('pwa.update-dismiss'),
      accept: () => {
        this._promptOpen = false;
        this._cancelReprompt();
        this._reload();
      },
      reject: () => {
        this._promptOpen = false;
        if (this._latestVersionHash !== promptedVersionHash) {
          this._promptDismissedAt = 0;
          this._cancelReprompt();
          this._maybePromptUpdate();
          return;
        }
        this._promptDismissedAt = Date.now();
        this._messageService.add({
          key: 'pwa',
          severity: 'info',
          summary: this._translate.instant('pwa.update-available'),
          life: 5000
        });
        this._scheduleReprompt();
      }
    });
  }

  private _scheduleReprompt(): void {
    this._cancelReprompt();
    this._repromptSubscription = timer(SW_UPDATE_PROMPT_COOLDOWN_MS).pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(() => {
      this._repromptSubscription = undefined;
      this._maybePromptUpdate();
    });
  }

  private _cancelReprompt(): void {
    this._repromptSubscription?.unsubscribe();
    this._repromptSubscription = undefined;
  }

  /** Indirection testabile: il reload reale interromperebbe il runner browser. */

  private _isEmbeddedIframe(): boolean {
    try {
      if (window.self === window.top) {
        return false;
      }
      // Same-origin iframe (Karma, nested same-app): keep the update prompt.
      void window.top?.location.href;
      return false;
    } catch {
      // Cross-origin parent: satellite inside the AmSuite shell.
      return true;
    }
  }

  private _handleInstallationFailed(): void {
    this._messageService.add({
      key: 'pwa',
      severity: 'warn',
      summary: this._translate.instant('pwa.installation-failed-title'),
      detail: this._translate.instant('pwa.installation-failed-message'),
      life: 8000
    });
  }

  private async _activateAndReload(): Promise<void> {
    try {
      await this._swUpdate.activateUpdate();
    } catch {
      // Reload anyway: the new index.html is already on the origin.
    }
    this._reload();
  }

  private _reload(): void {
    window.location.reload();
  }
}
